import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  InputAdornment,
  LinearProgress,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import CloseIcon from "mdi-material-ui/Close";
import RefreshIcon from "mdi-material-ui/Refresh";
import SearchIcon from "mdi-material-ui/Magnify";
import WrenchIcon from "mdi-material-ui/Wrench";
import { useSnackbar } from "notistack";
import useCurrentShopId from "../../../../hooks/useCurrentShopId";
import ColumnVisibilityMenu from "../../shared/components/ColumnVisibilityMenu";
import inventoryDiagnosticsQuery from "../graphql/queries/inventoryDiagnostics";
import fixInventoryDiagnosticItemMutation from "../graphql/mutations/fixInventoryDiagnosticItem";
import InventoryBatchActions, { DiagnosticProductLink, useInventorySelection } from "./InventoryBatchActions";
import ChangedValue, { changeValueText } from "./ChangedValue";

const BOOLEAN_FILTER_OPTIONS = [
  { label: "Alle", value: "" },
  { label: "Ja", value: "yes" },
  { label: "Nein", value: "no" }
];

const STORE_FILTER_OPTIONS = [
  { label: "Alle", value: "" },
  { label: "sichtbar", value: "sichtbar" },
  { label: "blockiert", value: "blockiert" }
];

const DEFAULT_VISIBLE_COLUMN_IDS = [
  "details",
  "problem",
  "productNumber",
  "serialNumber",
  "title",
  "virtualProductSuffix",
  "inventoryInStock",
  "inventoryReserved",
  "expectedReserved",
  "storeStatus"
];

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 0
  },
  toolbar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(3)
  },
  search: {
    flex: "1 1 320px",
    maxWidth: 640
  },
  tableWrap: {
    overflow: "auto",
    maxHeight: "65vh",
    "& th": { whiteSpace: "nowrap", fontWeight: 600 },
    "& tbody td": { paddingTop: theme.spacing(1.5), paddingBottom: theme.spacing(1.5) }
  },
  surface: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)"
  },
  heading: { fontWeight: 700, marginBottom: theme.spacing(1) },
  intro: { maxWidth: 780, marginBottom: theme.spacing(3) },
  summary: {
    display: "flex", alignItems: "center", flexWrap: "wrap",
    justifyContent: "space-between", gap: theme.spacing(1),
    padding: theme.spacing(0, 3, 2)
  },
  progress: { height: 3 },
  empty: { padding: theme.spacing(6, 3), textAlign: "center" },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "flex-end" },
    "& .MuiTablePagination-spacer": { flex: "1 1 auto" }
  },
  problem: {
    maxWidth: 360, height: "auto",
    "& .MuiChip-label": { whiteSpace: "normal", paddingTop: 5, paddingBottom: 5 }
  },
  changed: { backgroundColor: "#fff3cd", color: "#4a3700" },
  drawer: {
    padding: theme.spacing(3)
  },
  drawerPaper: {
    width: 920,
    maxWidth: "100vw"
  },
  detailsTable: {
    "& td": {
      verticalAlign: "top"
    }
  },
  muted: {
    color: theme.palette.text.secondary
  },
  issueList: {
    margin: 0,
    paddingLeft: theme.spacing(2)
  },
  filterCell: {
    backgroundColor: theme.palette.background.default,
    top: 48,
    minWidth: 120,
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    verticalAlign: "top"
  },
  actions: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  }
}));

function booleanText(value) {
  if (value === true) return "Ja";
  if (value === false) return "Nein";
  return "-";
}

function valueText(value) {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "boolean") return booleanText(value);
  if (value === null || typeof value === "undefined" || value === "") return "-";
  return String(value);
}

function csvValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(" | ") : "";
  if (typeof value === "boolean") return booleanText(value);
  if (value === null || typeof value === "undefined") return "";
  return String(value);
}

function escapeCsv(value) {
  const normalizedValue = csvValue(value).replace(/"/g, "\"\"");
  return `"${normalizedValue}"`;
}

function statusColor(item) {
  if (item.inventoryMismatch) return "secondary";
  if (item.reservedTooHigh || item.blockedByCurrentReserved) return "secondary";
  if (item.catalogMissing || item.catalogVariantMissing || item.catalogHasNoGroups || item.catalogGroupMissing) return "primary";
  return "default";
}

function isStoreVisible(item) {
  return item.catalogProductIsVisible === true &&
    !item.catalogProductIsSoldOut &&
    !item.catalogGroupIsSoldOut &&
    !item.catalogMissing &&
    !item.catalogVariantMissing &&
    !item.catalogHasNoGroups &&
    !item.catalogGroupMissing;
}

function storeStatusText(item) {
  return isStoreVisible(item) ? "sichtbar" : "blockiert";
}

function DetailRow({ label, value }) {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <strong>{label}</strong>
      </TableCell>
      <TableCell>{valueText(value)}</TableCell>
    </TableRow>
  );
}

const EXPORT_COLUMNS = [
  ["problem", (item) => item.issues?.[0] || "Keine akute Inkonsistenz"],
  ["issues", (item) => item.issues],
  ["productNumber", (item) => item.productNumber],
  ["serialNumber", (item) => item.serialNumber],
  ["title", (item) => item.title],
  ["virtualProductSuffix", (item) => item.virtualProductSuffix],
  ["inventoryInStock", (item) => item.inventoryInStock],
  ["expectedInventoryInStock", (item) => item.expectedInventoryInStock],
  ["inventoryReserved", (item) => item.inventoryReserved],
  ["expectedReserved", (item) => item.expectedReserved],
  ["storeStatus", (item) => storeStatusText(item)],
  ["productId", (item) => item.productId],
  ["variantId", (item) => item.variantId],
  ["limesId", (item) => item.limesId],
  ["storage", (item) => item.storage],
  ["backendAvailable", (item) => item.backendAvailable],
  ["publishableForShop", (item) => item.publishableForShop],
  ["productIsVisible", (item) => item.productIsVisible],
  ["variantIsVisible", (item) => item.variantIsVisible],
  ["variantStatus", (item) => item.variantStatus],
  ["technicalStatus", (item) => item.technicalStatus],
  ["sellStatus", (item) => item.sellStatus],
  ["showOnPricelist", (item) => item.showOnPricelist],
  ["price", (item) => item.price],
  ["currentAvailableToSell", (item) => item.currentAvailableToSell],
  ["expectedAvailableToSell", (item) => item.expectedAvailableToSell],
  ["orderIds", (item) => item.orderIds],
  ["reservationIds", (item) => item.reservationIds],
  ["catalogId", (item) => item.catalogId],
  ["catalogProductIsVisible", (item) => item.catalogProductIsVisible],
  ["catalogProductIsSoldOut", (item) => item.catalogProductIsSoldOut],
  ["catalogGroupIsSoldOut", (item) => item.catalogGroupIsSoldOut],
  ["catalogGroupAvailableToSell", (item) => item.catalogGroupAvailableToSell]
];

function formatInterfaceLogTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("de-DE");
}

export default function InventoryDiagnostics({ isActive = true }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [shopId] = useCurrentShopId();
  const [queryState, setQueryState] = useState({ page: 0, search: "", filters: {} });
  const { page, search: appliedSearch, filters: appliedFilters } = queryState;
  const setPage = useCallback((nextPage) => setQueryState((previous) => ({ ...previous, page: nextPage })), []);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [search, setSearch] = useState("");
  const [hasRequestedLoad, setHasRequestedLoad] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sortBy, setSortBy] = useState("productNumber");
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnFilters, setColumnFilters] = useState({});
  const [visibleColumnIds, setVisibleColumnIds] = useState(DEFAULT_VISIBLE_COLUMN_IDS);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryState({ search: search.trim(), filters: columnFilters, page: 0 });
    }, 400);
    return () => clearTimeout(timer);
  }, [search, columnFilters]);

  const input = useMemo(() => ({
    first: rowsPerPage,
    offset: page * rowsPerPage,
    mode: "all",
    includeHealthy: true,
    shopIds: shopId ? [shopId] : [],
    search: appliedSearch || null,
    sortBy,
    sortDirection,
    filters: Object.entries(appliedFilters)
      .filter(([, value]) => value !== "")
      .map(([field, value]) => ({ field, value }))
  }), [appliedFilters, appliedSearch, page, rowsPerPage, shopId, sortBy, sortDirection]);

  const requestController = useMemo(() => new AbortController(), [input]);
  useEffect(() => () => requestController.abort(), [requestController]);
  const requestContext = useMemo(() => ({
    fetchOptions: { signal: requestController.signal }
  }), [requestController]);

  const { data, loading: queryLoading, networkStatus, error, refetch } = useQuery(inventoryDiagnosticsQuery, {
    variables: { input },
    context: requestContext,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    pollInterval: isActive && hasRequestedLoad ? 15000 : 0,
    skip: !isActive || !shopId || !hasRequestedLoad
  });
  const loading = queryLoading && networkStatus !== 6 && networkStatus !== 4;

  const [fixItem, { loading: isFixing }] = useMutation(fixInventoryDiagnosticItemMutation);

  const nodes = data?.inventoryDiagnostics?.nodes || [];
  const totalCount = data?.inventoryDiagnostics?.totalCount || 0;
  const selection = useInventorySelection(JSON.stringify(input), !loading && !error ? nodes : [], "variantId");
  const originalValues = useRef(new Map());
  const originalKey = JSON.stringify(input);
  useEffect(() => { originalValues.current = new Map(); }, [originalKey]);
  useEffect(() => {
    if (!loading && !error) for (const row of nodes) {
      if (!originalValues.current.has(row.variantId)) originalValues.current.set(row.variantId, row);
    }
  }, [data, loading, error, originalKey]);

  useEffect(() => {
    if (!loading && data && page > 0 && page * rowsPerPage >= totalCount) {
      setPage(Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1));
    }
  }, [data, loading, page, rowsPerPage, setPage, totalCount]);

  const columns = useMemo(() => ([
    {
      id: "details",
      label: "Details",
      hideable: false,
      filterType: null,
      sortable: false,
      render: (item) => (
        <Button size="small" variant="outlined" onClick={() => setSelectedItem(item)}>
          Öffnen
        </Button>
      )
    },
    {
      id: "problem",
      label: "Problem",
      filterType: "text",
      getValue: (item) => item.issues?.[0] || "Keine akute Inkonsistenz",
      render: (item) => (
        <Chip
          className={classes.problem}
          size="small"
          color={statusColor(item)}
          label={item.issues?.[0] || "Keine akute Inkonsistenz"}
        />
      ),
      sortable: true
    },
    {
      id: "productNumber",
      label: "Produktnummer",
      filterType: "text",
      getValue: (item) => item.productNumber || "",
      render: (item) => <DiagnosticProductLink productId={item.productId} productNumber={item.productNumber} />,
      sortable: true
    },
    {
      id: "serialNumber",
      label: "Seriennummer",
      filterType: "text",
      getValue: (item) => item.serialNumber || "",
      sortable: true
    },
    {
      id: "title",
      label: "Titel",
      filterType: "text",
      getValue: (item) => item.title || "",
      sortable: true
    },
    {
      id: "virtualProductSuffix",
      label: "Suffix",
      filterType: "text",
      getValue: (item) => item.virtualProductSuffix || "",
      sortable: true
    },
    {
      id: "inventoryInStock",
      label: "Bestand",
      filterType: "number",
      getValue: (item) => item.inventoryInStock,
      sortable: true
    },
    {
      id: "expectedInventoryInStock",
      label: "Erwarteter Bestand",
      filterType: "number",
      getValue: (item) => item.expectedInventoryInStock,
      sortable: true
    },
    {
      id: "inventoryReserved",
      label: "Reserviert",
      filterType: "number",
      getValue: (item) => item.inventoryReserved,
      sortable: true
    },
    {
      id: "expectedReserved",
      label: "Erwartet reserviert",
      filterType: "number",
      getValue: (item) => item.expectedReserved,
      sortable: true
    },
    {
      id: "storeStatus",
      label: "Store-Status",
      filterType: "select",
      filterOptions: STORE_FILTER_OPTIONS,
      getValue: (item) => storeStatusText(item),
      sortable: true
    },
    {
      id: "vendor",
      label: "Vendor",
      filterType: "text",
      getValue: (item) => item.vendor || "",
      sortable: true
    },
    {
      id: "slug",
      label: "Slug",
      filterType: "text",
      getValue: (item) => item.slug || "",
      sortable: true
    },
    {
      id: "productId",
      label: "Produkt-ID",
      filterType: "text",
      getValue: (item) => item.productId || "",
      sortable: true
    },
    {
      id: "variantId",
      label: "Variant-ID",
      filterType: "text",
      getValue: (item) => item.variantId || "",
      sortable: true
    },
    {
      id: "inventoryId",
      label: "Inventory-ID",
      filterType: "text",
      getValue: (item) => item.inventoryId || "",
      sortable: true
    },
    {
      id: "shopId",
      label: "Shop-ID",
      filterType: "text",
      getValue: (item) => item.shopId || "",
      sortable: true
    },
    {
      id: "limesId",
      label: "Limes-ID",
      filterType: "text",
      getValue: (item) => item.limesId || "",
      sortable: true
    },
    {
      id: "storage",
      label: "Storage",
      filterType: "text",
      getValue: (item) => item.storage || "",
      sortable: true
    },
    {
      id: "backendAvailable",
      label: "Backend verfuegbar",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.backendAvailable,
      sortable: true
    },
    {
      id: "publishableForShop",
      label: "Publizierbar",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.publishableForShop,
      sortable: true
    },
    {
      id: "productIsVisible",
      label: "Produkt sichtbar",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.productIsVisible,
      sortable: true
    },
    {
      id: "variantIsVisible",
      label: "Lagerteil sichtbar",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.variantIsVisible,
      sortable: true
    },
    {
      id: "variantStatus",
      label: "Variant Status",
      filterType: "text",
      getValue: (item) => item.variantStatus || "",
      sortable: true
    },
    {
      id: "technicalStatus",
      label: "Technical Status",
      filterType: "text",
      getValue: (item) => item.technicalStatus || "",
      sortable: true
    },
    {
      id: "sellStatus",
      label: "Sell Status",
      filterType: "text",
      getValue: (item) => item.sellStatus || "",
      sortable: true
    },
    {
      id: "showOnPricelist",
      label: "Show On Pricelist",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.showOnPricelist,
      sortable: true
    },
    {
      id: "price",
      label: "Preis",
      filterType: "number",
      getValue: (item) => item.price,
      sortable: true
    },
    {
      id: "currentAvailableToSell",
      label: "Available To Sell",
      filterType: "number",
      getValue: (item) => item.currentAvailableToSell,
      sortable: true
    },
    {
      id: "expectedAvailableToSell",
      label: "Expected Available",
      filterType: "number",
      getValue: (item) => item.expectedAvailableToSell,
      sortable: true
    },
    {
      id: "orderIds",
      label: "Orders",
      filterType: "text",
      getValue: (item) => (item.orderIds || []).join(", "),
      sortable: true
    },
    {
      id: "reservationIds",
      label: "Reservations",
      filterType: "text",
      getValue: (item) => (item.reservationIds || []).join(", "),
      sortable: true
    },
    {
      id: "catalogId",
      label: "Catalog-ID",
      filterType: "text",
      getValue: (item) => item.catalogId || "",
      sortable: true
    },
    {
      id: "catalogProductIsVisible",
      label: "Catalog Produkt sichtbar",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.catalogProductIsVisible,
      sortable: true
    },
    {
      id: "catalogProductIsSoldOut",
      label: "Catalog Produkt sold out",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.catalogProductIsSoldOut,
      sortable: true
    },
    {
      id: "catalogGroupIsSoldOut",
      label: "Catalog Gruppe sold out",
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (item) => item.catalogGroupIsSoldOut,
      sortable: true
    },
    {
      id: "catalogGroupAvailableToSell",
      label: "Catalog Gruppenbestand",
      filterType: "number",
      getValue: (item) => item.catalogGroupAvailableToSell,
      sortable: true
    }
  ]), [classes.problem]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleColumnIds.includes(column.id)),
    [columns, visibleColumnIds]
  );

  const handleExportCsv = () => {
    if (!nodes.length) return;

    const header = EXPORT_COLUMNS.map(([column]) => escapeCsv(column)).join(",");
    const rows = nodes.map((item) => EXPORT_COLUMNS
      .map(([, getter]) => escapeCsv(getter(item)))
      .join(","));
    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const searchSuffix = appliedSearch ? `_${appliedSearch.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "")}` : "";

    link.href = url;
    link.download = `inventory-diagnostics${searchSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleRefresh = async () => {
    const nextSearch = search.trim();
    const variablesChanged = page !== 0 || appliedSearch !== nextSearch || appliedFilters !== columnFilters;
    setQueryState({ search: nextSearch, filters: columnFilters, page: 0 });
    if (!hasRequestedLoad) {
      setHasRequestedLoad(true);
      return;
    }
    if (!variablesChanged) {
      try {
        await refetch();
      } catch (refreshError) {
        enqueueSnackbar(refreshError.message, { variant: "error" });
      }
    }
  };

  const handleFix = async () => {
    if (!selectedItem) return;

    try {
      const { data: fixData } = await fixItem({
        variables: {
          input: {
            productVariantId: selectedItem.variantId
          }
        }
      });

      enqueueSnackbar((fixData?.fixInventoryDiagnosticItem?.actions || ["Daten korrigiert"]).join(", "), {
        variant: "success"
      });
      await refetch();
      setSelectedItem(fixData?.fixInventoryDiagnosticItem?.item || null);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const handleSort = (columnId) => {
    setPage(0);
    if (sortBy === columnId) {
      setSortDirection((currentDirection) => (currentDirection === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnId);
    setSortDirection("asc");
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((previousFilters) => ({
      ...previousFilters,
      [columnId]: value
    }));
  };

  const handleToggleColumn = (columnId) => {
    const column = columns.find((item) => item.id === columnId);

    if (!column || column.hideable === false) return;

    setVisibleColumnIds((previousIds) => (
      previousIds.includes(columnId)
        ? previousIds.filter((id) => id !== columnId)
        : [...previousIds, columnId]
    ));
  };

  const handleShowAllColumns = () => {
    setVisibleColumnIds(columns.map((column) => column.id));
  };

  const renderFilterInput = (column) => {
    const filterValue = columnFilters[column.id] || "";

    if (!column.filterType) {
      return null;
    }

    if (column.filterType === "select" || column.filterType === "boolean") {
      return (
        <TextField
          select
          fullWidth
          size="small"
          variant="outlined"
          inputProps={{ "aria-label": `${column.label} filtern` }}
          value={filterValue}
          onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
        >
          {(column.filterOptions || []).map((option) => (
            <MenuItem key={`${column.id}-${option.value || "all"}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        type={column.filterType === "number" ? "number" : "text"}
        variant="outlined"
        inputProps={{ "aria-label": `${column.label} filtern` }}
        value={filterValue}
        onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
      />
    );
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.heading}>Bestandsdiagnose</Typography>
      <Typography className={`${classes.muted} ${classes.intro}`} variant="body1">
        Prüfen Sie Lagerbestand, Reservierungen und Catalog-Daten. Finden Sie Lagerteile,
        die im Store fehlen oder nicht bestellbar sind, und beheben Sie Inkonsistenzen direkt.
      </Typography>

      <Paper className={classes.surface} elevation={0}>
        <Box className={classes.toolbar}>
          <TextField
            className={classes.search}
            label="Lagerteile suchen"
            placeholder="Produktnummer, Seriennummer, Limes-ID oder ID"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                handleRefresh();
              }
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
          <Box className={classes.actions}>
            <Button
              onClick={handleExportCsv}
              disabled={loading || Boolean(error) || !hasRequestedLoad || nodes.length === 0}
              size="small"
              variant="outlined"
            >
              CSV · diese Seite
            </Button>
            <Button color="primary" variant="contained" disableElevation startIcon={<RefreshIcon />}
              onClick={handleRefresh} disabled={loading || !shopId}>
              {hasRequestedLoad ? "Aktualisieren" : "Prüfung starten"}
            </Button>
          </Box>
        </Box>

        <Box className={classes.actions} px={3} pb={2}>
          <InventoryBatchActions shopId={shopId} selection={selection} pageCount={nodes.length}
            disabled={loading || Boolean(error) || !hasRequestedLoad} onComplete={async () => { await refetch(); setSelectedItem(null); }} />
          <ColumnVisibilityMenu columns={columns} visibleColumnIds={visibleColumnIds}
            onToggleColumn={handleToggleColumn} onShowAll={handleShowAllColumns} title="Spalten" />
          <Typography variant="caption" className={classes.muted}>Auswahl gilt für diese Seite · maximal 500 Lagerteile pro Batch</Typography>
          <Typography variant="caption" className={classes.muted}>Aktualisierung alle 15 Sekunden · Gelb: geändert seit Öffnen dieser Ansicht. Hover zeigt den ursprünglichen Wert.</Typography>
        </Box>

        <Box className={classes.summary}>
          <Typography variant="body2" className={classes.muted} role="status" aria-live="polite">
            {loading ? "Lagerteile werden geprüft …" : hasRequestedLoad
              ? `${totalCount.toLocaleString("de-DE")} Treffer · Alle Lagerteile`
              : "Bereit zur Prüfung · Alle Lagerteile"}
          </Typography>
          {(search || Object.values(columnFilters).some(Boolean)) && (
            <Button size="small" onClick={() => { setSearch(""); setColumnFilters({}); }}>
              Filter zurücksetzen
            </Button>
          )}
        </Box>
        {queryLoading ? <LinearProgress className={classes.progress} /> : <Divider />}
        {error && (
          <Box p={3} role="alert">
            <Typography color="error">Die Diagnose konnte nicht geladen werden. Bitte erneut aktualisieren.</Typography>
          </Box>
        )}
        <Box className={classes.tableWrap}>
          <Table stickyHeader size="small" aria-label="Inventory Diagnose">
            <TableHead>
              <TableRow style={{ height: 48 }}>
                <TableCell padding="checkbox">Auswahl</TableCell>
                {visibleColumns.map((column) => (
                  <TableCell key={column.id} sortDirection={sortBy === column.id ? sortDirection : false}>
                    {column.sortable === false ? (
                      column.label
                    ) : (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDirection : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell padding="checkbox" className={classes.filterCell} />
                {visibleColumns.map((column) => (
                  <TableCell key={`${column.id}-filter`} className={classes.filterCell}>
                    {renderFilterInput(column)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!loading && !hasRequestedLoad && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className={classes.empty}>
                    <Typography variant="h6" gutterBottom>Bestände und Bestellbarkeit prüfen</Typography>
                    <Typography className={classes.muted}>Starten Sie die Prüfung. Anschließend können Sie alle Ergebnisse durchsuchen, filtern und sortieren.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && hasRequestedLoad && nodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className={classes.empty}>
                    <Typography variant="h6" gutterBottom>Keine passenden Lagerteile</Typography>
                    <Typography className={classes.muted}>Passen Sie die Suche an oder setzen Sie die Filter zurück.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && hasRequestedLoad && nodes.map((item) => (
                <TableRow key={item.variantId} hover selected={selection.isSelected(item.variantId)}>
                  <TableCell padding="checkbox"><Checkbox color="primary" checked={selection.isSelected(item.variantId)}
                    onChange={() => selection.toggle(item.variantId)} inputProps={{ "aria-label": `${item.productNumber} / ${item.serialNumber || item.variantId} auswählen` }} /></TableCell>
                  {visibleColumns.map((column) => {
                    const original = originalValues.current.get(item.variantId);
                    const previous = original && column.getValue ? column.getValue(original) : undefined;
                    const currentValue = column.getValue ? column.getValue(item) : undefined;
                    const changed = original && column.getValue && changeValueText(previous) !== changeValueText(currentValue);
                    return <TableCell key={`${item.variantId}-${column.id}`} className={changed ? classes.changed : undefined}>
                      <ChangedValue label={column.label} previousValue={changed ? changeValueText(previous) : undefined} currentValue={currentValue}>
                        {column.render ? column.render(item) : valueText(currentValue)}
                      </ChangedValue>
                    </TableCell>;
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          className={classes.pagination}
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100, 200, 250, 500]}
          onChangePage={(event, nextPage) => setPage(nextPage)}
          onChangeRowsPerPage={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          labelRowsPerPage="Zeilen pro Seite"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count.toLocaleString("de-DE")}`}
          ActionsComponent={({ count, page: currentPage, rowsPerPage: pageSize, onChangePage }) => (
            <Box ml={2}>
              <Pagination
                color="primary"
                shape="rounded"
                size="small"
                showFirstButton
                showLastButton
                siblingCount={0}
                count={Math.max(1, Math.ceil(count / pageSize))}
                page={currentPage + 1}
                disabled={loading || Boolean(error) || !hasRequestedLoad}
                onChange={(event, nextPage) => onChangePage(event, nextPage - 1)}
                getItemAriaLabel={(type, itemPage) => ({
                  page: `Seite ${itemPage}`,
                  first: "Erste Seite",
                  last: "Letzte Seite",
                  next: "Nächste Seite",
                  previous: "Vorherige Seite"
                }[type] || "Weitere Seiten")}
              />
            </Box>
          )}
          backIconButtonText="Vorherige Seite"
          nextIconButtonText="Nächste Seite"
          backIconButtonProps={{ disabled: loading || page === 0 }}
          nextIconButtonProps={{ disabled: loading || !hasRequestedLoad || (page + 1) * rowsPerPage >= totalCount }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        PaperProps={{ className: classes.drawerPaper }}
      >
        {selectedItem && (
          <Box className={classes.drawer}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{selectedItem.productNumber}</Typography>
                <Typography className={classes.muted}>{selectedItem.serialNumber}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedItem(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box mt={2} mb={2}>
              <Typography variant="subtitle1">Probleme</Typography>
              <ul className={classes.issueList}>
                {(selectedItem.issues || ["Keine akute Inkonsistenz gefunden."]).map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle1">Beheben</Typography>
              <Typography className={classes.muted}>{selectedItem.fixDescription}</Typography>
              <Box mt={1}>
                <Button
                  color="primary"
                  disabled={isFixing}
                  onClick={handleFix}
                  startIcon={<WrenchIcon />}
                  variant="contained"
                >
                  Beheben
                </Button>
              </Box>
            </Box>

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Details</Typography>
                <Table size="small" className={classes.detailsTable}>
                  <TableBody>
                    <DetailRow label="Titel" value={selectedItem.title} />
                    <DetailRow label="Beschreibung" value={selectedItem.description || selectedItem.salesDescription} />
                    <DetailRow label="Produkt-ID" value={selectedItem.productId} />
                    <DetailRow label="Variant-ID" value={selectedItem.variantId} />
                    <DetailRow label="Limes-ID" value={selectedItem.limesId} />
                    <DetailRow label="Storage" value={selectedItem.storage} />
                    <DetailRow label="Backend verfuegbar" value={selectedItem.backendAvailable} />
                    <DetailRow label="Publizierbar fuer Shop" value={selectedItem.publishableForShop} />
                    <DetailRow label="Produkt sichtbar" value={selectedItem.productIsVisible} />
                    <DetailRow label="Lagerteil sichtbar" value={selectedItem.variantIsVisible} />
                    <DetailRow label="Status / Technik / Verkauf" value={`${valueText(selectedItem.variantStatus)} / ${valueText(selectedItem.technicalStatus)} / ${valueText(selectedItem.sellStatus)}`} />
                    <DetailRow label="Show on Pricelist" value={selectedItem.showOnPricelist} />
                    <DetailRow label="Preis" value={selectedItem.price} />
                    <DetailRow label="Inventory in Stock" value={selectedItem.inventoryInStock} />
                    <DetailRow label="Erwarteter Inventory-Bestand" value={selectedItem.expectedInventoryInStock} />
                    <DetailRow label="Inventory Reserved" value={selectedItem.inventoryReserved} />
                    <DetailRow label="Expected Reserved" value={selectedItem.expectedReserved} />
                    <DetailRow label="Current Available to Sell" value={selectedItem.currentAvailableToSell} />
                    <DetailRow label="Expected Available to Sell" value={selectedItem.expectedAvailableToSell} />
                    <DetailRow label="Orders" value={selectedItem.orderIds} />
                    <DetailRow label="Reservations" value={selectedItem.reservationIds} />
                    <DetailRow label="Catalog-ID" value={selectedItem.catalogId} />
                    <DetailRow label="Catalog Produkt sichtbar" value={selectedItem.catalogProductIsVisible} />
                    <DetailRow label="Catalog Produkt ausverkauft" value={selectedItem.catalogProductIsSoldOut} />
                    <DetailRow label="Catalog Gruppe ausverkauft" value={selectedItem.catalogGroupIsSoldOut} />
                    <DetailRow label="Catalog Gruppe Bestand" value={selectedItem.catalogGroupAvailableToSell} />
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Schnittstellen-Logs</Typography>
                {(selectedItem.interfaceLogs || []).length === 0 ? (
                  <Typography className={classes.muted}>Noch keine gespeicherten Limes-Updates fuer dieses Lagerteil.</Typography>
                ) : (
                  <Table size="small" className={classes.detailsTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Zeitpunkt</TableCell>
                        <TableCell>Quelle</TableCell>
                        <TableCell>Ergebnis</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedItem.interfaceLogs.map((log, index) => (
                        <TableRow key={`${log.createdAt || "log"}-${index}`}>
                          <TableCell>{formatInterfaceLogTime(log.createdAt)}</TableCell>
                          <TableCell>{valueText(log.status ? `${log.source || log.operation} (${log.status})` : (log.source || log.operation))}</TableCell>
                          <TableCell>{valueText(log.message)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
